import {
  ContentStatus,
  GetFolderPageData,
  GetFolderPageDataVariables,
} from "__generated__/types";
import { MultilineText } from "common/components/MultilineText";
import { NoDataError } from "common/components/NoDataError";
import { SubText } from "common/components/Typography";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NormalTabbedLayout } from "common/components/layouts/NormalTabbedLayout";
import { ContentElementList } from "common/components/lists/content/ContentElementList";
import { folderElementListFragment } from "common/components/lists/content/fragments";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import { contentBreadcrumbFragment } from "domain/pages/breadcrumbs";
import gql from "graphql-tag";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

type RouteProps = {
  folderId: string;
};

const DATA_QUERY = gql`
  query GetFolderPageData($folderId: ID!) {
    folderById(id: $folderId) {
      ...ContentBreadcrumbFragment
      ...FolderElementListFragment
    }
  }
  ${contentBreadcrumbFragment}
  ${folderElementListFragment}
`;

export const FolderPage: React.FC = () => {
  const { folderId } = useParams<RouteProps>();

  if (!folderId) {
    throw new Error("Unexpected error: Missing folderId");
  }

  const { loadingOrError, data } = useSimpleQuery<
    GetFolderPageData,
    GetFolderPageDataVariables
  >(DATA_QUERY, {
    variables: {
      folderId,
    },
  });

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.folderById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const content =
    data.folderById.elements.filter(
      (e) => e.content.status === ContentStatus.PUBLISHED,
    ).length === 0 ? (
      <div style={{ padding: 20 }}>
        Denne mappen er helt tom. Gi beskjed til fagansvarlig dersom dette ikke
        er riktig.
      </div>
    ) : (
      <ContentElementList parent={data.folderById} />
    );

  return (
    <NormalTabbedLayout
      title={data.folderById.title}
      context={data.folderById.context ?? []}
      navigationTabs={[
        {
          title: "Innhold",
          path: urls.folder(folderId),
          content: content,
        },
        {
          title: "Beskrivelse",
          path: urls.folderDescription(folderId),
          content: (
            <>
              {data.folderById.description ? (
                <MultilineText {...data.folderById.description} />
              ) : (
                <p>Ingen beskrivelse er oppgitt.</p>
              )}
            </>
          ),
        },
      ]}
      footer={
        data.folderById.canUpdateDetails.canPerform ? (
          <SubText>
            <Link to={urls.folderAdminEdit(data.folderById.id)}>
              Rediger mappedetaljer
            </Link>
            <br />
            <Link to={urls.folderAdmin(data.folderById.id)}>
              Rediger mappeinnhold
            </Link>
          </SubText>
        ) : undefined
      }
    />
  );
};
